import React, { FC, useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, Typography, alpha, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store";
import { fetchTables, listenToMatchs } from "../../features/matchs/matchsSlice";
import {
  fetchBoards,
  fetchTournoi,
  getBoards,
  getTournoi,
} from "../../features/tournoi/tournoiSlice";
import LiveDirectory from "./LiveDirectory";
import LiveFindHandheld from "./LiveFindHandheld";
import type { LivePlayerEntry } from "./liveDirectoryTypes";
import { normalizeBoardLiveColor } from "../../utils/boardLiveColor";
import { computeLiveLayout, computeLivePageVisual } from "./liveLayout";

/** Mise en évidence des matchs lancés dans les 4 dernières minutes. */
const RECENT_LAUNCH_MS = 4 * 60_000;

function requestFullscreen(): void {
  const el = document.documentElement;
  if (el.requestFullscreen) el.requestFullscreen();
  else if (
    (el as HTMLElement & { webkitRequestFullscreen?: () => void })
      .webkitRequestFullscreen
  ) {
    (
      el as HTMLElement & { webkitRequestFullscreen: () => void }
    ).webkitRequestFullscreen();
  }
}

/** Préfixe affiché : 1ʳᵉ lettre majuscule + 2ᵉ en minuscule (ex. « Ch »). */
function namePrefix(nom: string): string {
  const cleaned = nom.trim();
  if (!cleaned) return "?";
  const a = cleaned.charAt(0).toLocaleUpperCase("fr");
  const b = cleaned.charAt(1);
  if (!b) return a;
  return `${a}${b.toLocaleLowerCase("fr")}`;
}

function letterRange(entries: LivePlayerEntry[]): string {
  if (entries.length === 0) return "";
  const first = namePrefix(entries[0].nom);
  const last = namePrefix(entries[entries.length - 1].nom);
  return first === last ? first : `${first} → ${last}`;
}

const LiveFind: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const kioskParam = searchParams.get("kiosk") === "1";
  const isBelowLg = useMediaQuery(theme.breakpoints.down("lg"));

  const [pageIdx, setPageIdx] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [pageKey, setPageKey] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const tournoi = useAppSelector(getTournoi);
  const boards = useAppSelector(getBoards);
  const liveMatchs = useAppSelector((state) =>
    state.matchs.matchs.filter((m) => m.status === "ENCOURS")
  );

  const isKiosk = kioskParam || isFullScreen;
  /** Mobile / tablette hors kiosk : UI handheld. Desktop lg+ et kiosk : rendu TV inchangé. */
  const isHandheld = !isKiosk && isBelowLg;

  useEffect(() => {
    const id = "live-display-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800&family=Syne:wght@700;800&display=swap";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (!tournoi?.id) return;
    dispatch(fetchTables());
    dispatch(fetchTournoi());
    dispatch(fetchBoards());
    const unsubscribe = dispatch(listenToMatchs());
    return () => {
      unsubscribe();
    };
  }, [dispatch, tournoi?.id]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleFullScreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement ||
        (document as Document & { webkitFullscreenElement?: Element })
          .webkitFullscreenElement;
      setIsFullScreen(fullscreenElement != null);
    };
    document.addEventListener("fullscreenchange", handleFullScreenChange);
    document.addEventListener(
      "webkitfullscreenchange",
      handleFullScreenChange
    );
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullScreenChange
      );
    };
  }, []);

  const boardsById = useMemo(() => {
    const map = new Map<string, (typeof boards)[number]>();
    boards.forEach((b) => {
      if (b.id != null) map.set(String(b.id), b);
    });
    return map;
  }, [boards]);

  const entries: LivePlayerEntry[] = useMemo(() => {
    const list: LivePlayerEntry[] = [];
    for (const match of liveMatchs) {
      if (match.tableId == null || match.id == null) continue;
      const board = boardsById.get(String(match.tableauId));
      const boardColor = normalizeBoardLiveColor(
        board?.liveColor ?? match.tableau?.liveColor
      );
      const boardName = String(
        board?.name || match.tableau?.name || "?"
      ).trim();
      const boardLabel = String(
        board?.label || match.tableau?.label || boardName
      ).trim();
      const tourLabel = String(match.tour?.label || "").trim();
      const players = [...(match.matchsPlayerParticipations ?? [])].sort(
        (a, b) => (a.ordre ?? 0) - (b.ordre ?? 0)
      );
      for (const mp of players) {
        const nom = (mp.players?.name ?? "").trim().toLocaleUpperCase("fr");
        const prenom = (mp.players?.surname ?? "").trim();
        if (!nom && !prenom) continue;
        const launchedAtMs =
          match.dateLancement instanceof Date
            ? match.dateLancement.getTime()
            : null;
        list.push({
          key: `${match.id}-${mp.playersId ?? mp.id ?? nom}`,
          matchId: String(match.id),
          tableId: match.tableId,
          nom: nom || prenom,
          prenom: nom ? prenom : "",
          searchText: "",
          boardName,
          boardLabel,
          boardColor,
          tourLabel,
          launchedAtMs,
        });
      }
    }
    return list.sort((a, b) =>
      a.nom.localeCompare(b.nom, "fr", { sensitivity: "base" })
    );
  }, [liveMatchs, boardsById]);

  const layout = useMemo(
    () => computeLiveLayout(entries.length),
    [entries.length]
  );
  const { pageSize, pageDwellMs } = layout;
  const pageCount = Math.max(1, Math.ceil(entries.length / pageSize) || 1);

  const safePageIdx = pageIdx >= pageCount ? 0 : pageIdx;

  useEffect(() => {
    if (isHandheld) return;
    if (pageIdx !== safePageIdx) setPageIdx(safePageIdx);
  }, [isHandheld, pageIdx, safePageIdx]);

  useEffect(() => {
    if (isHandheld) return;
    if (entries.length === 0) return;
    // Une seule page : pas de rotation, juste un refresh périodique léger
    if (pageCount <= 1) {
      const timer = window.setInterval(() => {
        dispatch(fetchTournoi());
        dispatch(fetchBoards());
        setNowMs(Date.now());
      }, 30_000);
      return () => window.clearInterval(timer);
    }
    const timer = window.setInterval(() => {
      setPageIdx((prev) => {
        const next = prev + 1;
        return next < pageCount ? next : 0;
      });
      setPageKey((k) => k + 1);
    }, pageDwellMs);
    return () => window.clearInterval(timer);
  }, [isHandheld, entries.length, pageCount, pageDwellMs, dispatch]);

  // Refresh Firestore à chaque retour page 0 (fin de cycle)
  useEffect(() => {
    if (isHandheld) return;
    if (pageCount <= 1 || safePageIdx !== 0 || pageKey === 0) return;
    dispatch(fetchTournoi());
    dispatch(fetchBoards());
  }, [isHandheld, safePageIdx, pageKey, pageCount, dispatch]);

  const pageEntries = useMemo(() => {
    const start = safePageIdx * pageSize;
    return entries.slice(start, start + pageSize);
  }, [entries, safePageIdx, pageSize]);

  // Dernière page (ou page courte) : agrandit les cartes pour remplir l’écran
  const { columns, density } = useMemo(
    () => computeLivePageVisual(pageEntries.length),
    [pageEntries.length]
  );

  const enableKiosk = () => {
    const next = new URLSearchParams(searchParams);
    next.set("kiosk", "1");
    setSearchParams(next, { replace: true });
    requestFullscreen();
  };

  const rangeLabel = letterRange(pageEntries);
  const cycleSec =
    entries.length === 0 || pageCount <= 1
      ? 0
      : Math.round((pageCount * pageDwellMs) / 1000);

  if (isHandheld) {
    return (
      <LiveFindHandheld
        entries={entries}
        tournamentLabel={tournoi?.label ?? "Tournoi"}
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: isKiosk ? "100vh" : "calc(100vh - 120px)",
        height: isKiosk ? "100vh" : "auto",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        color: "#F4F7FB",
        background: `
          radial-gradient(1200px 600px at 10% -10%, ${alpha("#1B6CA8", 0.45)} 0%, transparent 55%),
          radial-gradient(900px 500px at 90% 10%, ${alpha("#FF8A3D", 0.18)} 0%, transparent 50%),
          linear-gradient(165deg, #071018 0%, #0B1F33 48%, #10263A 100%)
        `,
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          backgroundImage: `radial-gradient(${alpha("#fff", 0.04)} 1px, transparent 1px)`,
          backgroundSize: "22px 22px",
          maskImage:
            "linear-gradient(180deg, rgba(0,0,0,0.9), rgba(0,0,0,0.35))",
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          width: 520,
          height: 520,
          borderRadius: "50%",
          right: -120,
          top: "35%",
          background: `radial-gradient(circle, ${alpha("#FF8A3D", 0.14)} 0%, transparent 68%)`,
          animation: "liveAmbient 12s ease-in-out infinite",
          pointerEvents: "none",
          "@keyframes liveAmbient": {
            "0%, 100%": { transform: "translate(0, 0) scale(1)", opacity: 0.7 },
            "50%": { transform: "translate(-40px, -30px) scale(1.12)", opacity: 1 },
          },
        },
        userSelect: "none",
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          px: { xs: 2.5, md: 3.5, xl: 4 },
          pt: { xs: 1.75, md: 2.25 },
          pb: { xs: 1.75, md: 2.25 },
          gap: { xs: 1.75, md: 2.25 },
        }}
      >
        {/* Bandeau : titre+labels | progression centrée | lettres */}
        <Box
          sx={{
            flexShrink: 0,
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            columnGap: { xs: 2.5, md: 4 },
            minHeight: { xs: 88, md: 104 },
            py: { xs: 0.5, md: 0.75 },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: '"Outfit", sans-serif',
                fontSize: { xs: 11, md: 12 },
                fontWeight: 600,
                letterSpacing: 1.8,
                textTransform: "uppercase",
                color: alpha("#F4F7FB", 0.5),
                lineHeight: 1.25,
              }}
            >
              SQY Ping · Live
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Syne", sans-serif',
                fontWeight: 800,
                fontSize: { xs: "1.75rem", md: "2.35rem", xl: "2.65rem" },
                letterSpacing: -1.1,
                lineHeight: 1.1,
                mt: 0.55,
                background: `linear-gradient(110deg, #F4F7FB 0%, #F4F7FB 55%, ${alpha("#FF8A3D", 0.95)} 100%)`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                color: "transparent",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Où jouez-vous ?
            </Typography>
            <Typography
              sx={{
                mt: 0.65,
                fontFamily: '"Outfit", sans-serif',
                color: alpha("#F4F7FB", 0.55),
                fontSize: { xs: 13, md: 14 },
                lineHeight: 1.35,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {tournoi?.label ?? "Tournoi"}
              {" · "}
              {entries.length} joueur{entries.length > 1 ? "s" : ""} en match
              {cycleSec > 0 ? ` · cycle ~${cycleSec}s` : ""}
            </Typography>
          </Box>

          <Box
            sx={{
              width: { xs: 160, md: 240, xl: 280 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 0.75,
            }}
          >
            {pageCount > 1 && (
              <>
                <Stack direction="row" spacing={0.65} justifyContent="center">
                  {Array.from({ length: pageCount }, (_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: i === safePageIdx ? 20 : 6,
                        height: 6,
                        borderRadius: 999,
                        bgcolor:
                          i === safePageIdx
                            ? "#FF8A3D"
                            : alpha("#FFFFFF", 0.28),
                        boxShadow:
                          i === safePageIdx
                            ? `0 0 10px ${alpha("#FF8A3D", 0.5)}`
                            : "none",
                        transition:
                          "width 0.35s ease, background-color 0.35s ease",
                      }}
                    />
                  ))}
                </Stack>
                <Box
                  key={`progress-${pageKey}`}
                  sx={{
                    height: 2.5,
                    borderRadius: 999,
                    bgcolor: alpha("#fff", 0.12),
                    overflow: "hidden",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: "100%",
                      borderRadius: 999,
                      background: `linear-gradient(90deg, ${alpha("#FF8A3D", 0.55)}, #FF8A3D)`,
                      transformOrigin: "left center",
                      animation: `livePageProgress ${pageDwellMs}ms linear forwards`,
                      "@keyframes livePageProgress": {
                        from: { transform: "scaleX(0)" },
                        to: { transform: "scaleX(1)" },
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              minWidth: 0,
            }}
          >
            {entries.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: { xs: 2.5, md: 3.5 },
                  py: { xs: 1.25, md: 1.5 },
                  minWidth: { xs: 120, md: 160, xl: 176 },
                  minHeight: { xs: 52, md: 64, xl: 72 },
                  borderRadius: 2.5,
                  bgcolor: "#FF8A3D",
                  color: "#1A120C",
                  boxShadow: `0 6px 22px ${alpha("#FF8A3D", 0.42)}`,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Outfit", sans-serif',
                    fontWeight: 800,
                    fontSize: { xs: "1.85rem", md: "2.4rem", xl: "2.7rem" },
                    lineHeight: 1,
                    letterSpacing: -0.8,
                  }}
                >
                  {rangeLabel || "—"}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {!isKiosk && (
          <Button
            variant="contained"
            startIcon={<FullscreenIcon />}
            onClick={enableKiosk}
            sx={{
              position: "absolute",
              right: { xs: 16, md: 24 },
              bottom: { xs: 16, md: 24 },
              zIndex: 3,
              borderRadius: 999,
              px: 2,
              bgcolor: "#FF8A3D",
              color: "#1A120C",
              fontFamily: '"Outfit", sans-serif',
              fontWeight: 700,
              textTransform: "none",
              boxShadow: `0 8px 24px ${alpha("#FF8A3D", 0.35)}`,
              "&:hover": { bgcolor: "#FF9D5C" },
            }}
          >
            Écran TV
          </Button>
        )}

        <Box
          key={pageKey}
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            borderRadius: 3,
            p: { xs: 1, md: 1.5, xl: 1.75 },
            bgcolor: alpha("#000", 0.22),
            border: 1,
            borderColor: alpha("#fff", 0.1),
            boxShadow: `inset 0 1px 0 ${alpha("#fff", 0.06)}, 0 20px 50px ${alpha("#000", 0.25)}`,
            overflow: "hidden",
            animation: "livePageIn 480ms cubic-bezier(0.22, 1, 0.36, 1) both",
            "@keyframes livePageIn": {
              from: { opacity: 0, transform: "translateY(16px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <LiveDirectory
            pageEntries={pageEntries}
            columns={columns}
            empty={entries.length === 0}
            recentLaunchMs={RECENT_LAUNCH_MS}
            nowMs={nowMs}
            density={density}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default LiveFind;
